import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./pages/Anketa.css";

// эту залупу нейронка написала

interface Question {
    id: number;
    text: string;
    correctAnswer?: string;
}

interface QuestionnaireProps {
    questions: Question[];
    title?: string;
    onComplete?: (answers: Record<number, string>, hasWrongAnswer: boolean) => void;
    redirectPath?: string;
    showCorrectAnswers?: boolean;
    successMessage?: string;
    errorMessage?: string;
}

export const Questionnaire: React.FC<QuestionnaireProps> = ({
                                                                questions,
                                                                title = "Анкета",
                                                                onComplete,
                                                                redirectPath,
                                                                showCorrectAnswers = true,
                                                                successMessage = "Поздравляем! Вы успешно прошли анкету!",
                                                                errorMessage = "К сожалению, наши услуги не подходят"
                                                            }) => {
    const navigate = useNavigate();

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [isCompleted, setIsCompleted] = useState(false);
    const [hasWrongAnswer, setHasWrongAnswer] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    // Анимированный прогресс
    const [animatedProgress, setAnimatedProgress] = useState(0);

    // Расчет прогресса с анимацией
    useEffect(() => {
        const answeredCount = Object.keys(answers).length;
        const totalQuestions = questions.length;
        const targetProgress = (answeredCount / totalQuestions) * 100;

        // Анимация прогресса
        let animationFrame: number;
        const animate = (startTime: number) => {
            const duration = 500; // 0.5 секунды
            const currentTime = Date.now();
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Кубическая анимация
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            const newProgress = animatedProgress + (targetProgress - animatedProgress) * easeProgress;

            setAnimatedProgress(newProgress);

            if (progress < 1) {
                animationFrame = requestAnimationFrame(() => animate(startTime));
            }
        };

        setIsAnimating(true);
        const startTime = Date.now();
        animationFrame = requestAnimationFrame(() => animate(startTime));

        return () => {
            cancelAnimationFrame(animationFrame);
        };
    }, [answers, questions.length, animatedProgress]);

    const handleAnswer = (answer: string) => {
        const newAnswers = { ...answers, [questions[currentQuestionIndex].id]: answer };
        setAnswers(newAnswers);

        // Если это последний вопрос
        if (currentQuestionIndex === questions.length - 1) {
            // Проверяем, есть ли неправильные ответы
            let hasIncorrect = false;
            if (showCorrectAnswers) {
                hasIncorrect = checkForIncorrectAnswers(newAnswers);
            }
            setHasWrongAnswer(hasIncorrect);
            setIsCompleted(true);

            // Вызываем callback если передан
            if (onComplete) {
                onComplete(newAnswers, hasIncorrect);
            }

            // Редирект если передан путь
            if (redirectPath && !hasIncorrect) {
                navigate(redirectPath);
            }
        } else {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const checkForIncorrectAnswers = (userAnswers: Record<number, string>): boolean => {
        for (const question of questions) {
            if (question.correctAnswer) {
                const userAnswer = userAnswers[question.id];
                if (userAnswer && userAnswer !== question.correctAnswer) {
                    return true;
                }
            }
        }
        return false;
    };

    const getAnswerStyle = (answerType: string) => {
        const currentAnswer = answers[questions[currentQuestionIndex]?.id];
        if (currentAnswer === answerType) {
            return {
                backgroundColor: answerType === 'ДА' ? '#10b981' : '#ef4444',
                color: 'white',
                transform: 'scale(1.05)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            };
        }
        return {};
    };

    if (isCompleted) {
        return (
            <div className="anketa-container">
                <div className="completion-card">
                    {hasWrongAnswer ? (
                        <>
                            <h2 className="error-title">{title}</h2>
                            <div className="error-message">
                                <p>{errorMessage}</p>
                            </div>
                        </>
                    ) : (
                        <>
                            <h2 className="success-title">{title}</h2>
                            <div className="success-message">
                                <p>{successMessage}</p>
                            </div>
                        </>
                    )}

                    <div className="completion-actions">
                        <button onClick={() => navigate("/")} className="action-btn home-btn">
                            На главную
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="anketa-container">
            <div className="anketa-card">

                {/* Прогресс бар */}
                <div className="progress-section">
                    <div className="progress-header">
                        <span className="progress-text">
                            Вопрос {currentQuestionIndex + 1} из {questions.length}
                        </span>
                        <span className="progress-percentage">{Math.round(animatedProgress)}%</span>
                    </div>
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{
                                width: `${animatedProgress}%`,
                                transition: isAnimating ? 'none' : 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        ></div>
                    </div>
                </div>

                {/* Вопрос */}
                <div className="question-section">
                    <h2 className="question-text">
                        {questions[currentQuestionIndex]?.text}
                    </h2>
                    <p className="question-hint">
                        Выберите один из вариантов ответа:
                    </p>
                </div>

                {/* Варианты ответов */}
                <div className="answers-section">
                    <button
                        className="answer-btn yes-btn"
                        onClick={() => handleAnswer('ДА')}
                        style={getAnswerStyle('ДА')}
                    >
                        <span className="answer-text">ДА</span>
                    </button>

                    <button
                        className="answer-btn no-btn"
                        onClick={() => handleAnswer('НЕТ')}
                        style={getAnswerStyle('НЕТ')}
                    >
                        <span className="answer-text">НЕТ</span>
                    </button>
                </div>

                {/* Навигация */}
                <div className="navigation-section">
                    <div className="completion-info">
                        Отвечено: {Object.keys(answers).length} из {questions.length}
                    </div>

                    <button className="nav-btn home-btn" onClick={() => navigate("/")}>
                        На главную
                    </button>
                </div>

                {/* Индикатор текущего ответа */}
                {answers[questions[currentQuestionIndex]?.id] && (
                    <div className="current-answer-indicator">
                        Ваш текущий ответ:
                        <span className={`current-answer ${answers[questions[currentQuestionIndex].id] === 'ДА' ? 'yes' : 'no'}`}>
                            {answers[questions[currentQuestionIndex].id]}
                        </span>
                        <button
                            className="change-answer-btn"
                            onClick={() => {
                                const newAnswers = { ...answers };
                                delete newAnswers[questions[currentQuestionIndex].id];
                                setAnswers(newAnswers);
                            }}
                        >
                            Изменить
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};